import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { isAuthenticated } from '../../../services/authService';
import styles from './Footer.module.css';

const Footer = () => {
  const { t } = useTranslation();

  // Même mécanisme que la Navbar : /articles est une page authentifiée, le
  // lien ne doit apparaître que pour un utilisateur connecté.
  //
  // Au prérendu, localStorage est vide : le lien est donc absent du HTML
  // servi aux visiteurs non connectés et aux crawlers — ce qui est voulu,
  // /articles étant par ailleurs en Disallow dans robots.txt.
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());

  useEffect(() => {
    const checkAuth = () => setIsLoggedIn(isAuthenticated());

    checkAuth();
    // 'storage' : connexion/déconnexion depuis un autre onglet.
    // 'localStorageUpdated' : événement maison, même onglet (cf. Navbar).
    window.addEventListener('storage', checkAuth);
    window.addEventListener('localStorageUpdated', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('localStorageUpdated', checkAuth);
    };
  }, []);

  const socialLinks = {
    facebook: 'https://www.facebook.com/profile.php?id=61578699059346',
    instagram: 'https://www.instagram.com/flowdinosync/',
    youtube: 'https://www.youtube.com/@FlowDino-d8l',
    tiktok: 'https://www.tiktok.com/@flowdino_app'
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.grid}>
          <div className={styles.column}>
            <h3>{t('footer.about.title')}</h3>
            <p>{t('footer.about.description')}</p>
          </div>
          <div className={styles.column}>
            <h3>{t('footer.usefulLinks.title')}</h3>
            <ul className={styles.linkList}>
              <li><Link to="/pricing">{t('footer.usefulLinks.pricing')}</Link></li>
              <li><Link to="/blog">{t('footer.usefulLinks.blog')}</Link></li>
              <li><Link to="/documentation">{t('footer.usefulLinks.documentation')}</Link></li>
              <li><Link to="/faq">{t('footer.usefulLinks.faq')}</Link></li>
              {isLoggedIn && (
                <li><Link to="/articles">{t('footer.usefulLinks.articles')}</Link></li>
              )}
            </ul>
          </div>
          <div className={styles.column}>
            <h3>{t('footer.legal.title')}</h3>
            <ul className={styles.linkList}>
              <li><Link to="/cgu">{t('footer.legal.tos')}</Link></li>
              <li><Link to="/privacy">{t('footer.legal.privacy')}</Link></li>
              <li><Link to="/mentions-legales">{t('footer.legal.legalNotice')}</Link></li>
            </ul>
          </div>
          <div className={styles.column}>
            <h3>{t('footer.social.title')}</h3>
            <ul className={styles.linkList}>
              <li>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <FaFacebook className={styles.socialIcon} />
                  <span>{t('footer.social.facebook')}</span>
                </a>
              </li>
              <li>
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <FaInstagram className={styles.socialIcon} />
                  <span>{t('footer.social.instagram')}</span>
                </a>
              </li>
              <li>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                  <FaYoutube className={styles.socialIcon} />
                  <span>{t('footer.social.youtube')}</span>
                </a>
              </li>
              <li>
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer">
                  <FaTiktok className={styles.socialIcon} />
                  <span>{t('footer.social.tiktok')}</span>
                </a>
              </li>
            </ul>
          </div>
          <div className={styles.column}>
            <h3>{t('footer.contact.title')}</h3>
            <ul className={styles.linkList}>
              <li><a href="mailto:contact@flowdino.com">{t('footer.contact.email')}</a></li>
              <li><Link to="/support">{t('footer.contact.support')}</Link></li>
            </ul>
          </div>
        </div>
        <div className={styles.bottom}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
